import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import ListPicker from '@components/picker/ListPicker'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import Checkbox from '@components/checkbox/Checkbox'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import sanitize from '@utils/Sanitizer'

import { EtatWorkflowIncidentEnum } from '@api/enum'

interface Props {
  selected: EtatWorkflowIncidentEnum[]
  onStatusSelected: (items: EtatWorkflowIncidentEnum[]) => void
}

const DATA: EtatWorkflowIncidentEnum[] = [
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_VALIDATION,
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_PRISE_EN_CHARGE,
  EtatWorkflowIncidentEnum.REFUS_PRESTATAIRE,
  EtatWorkflowIncidentEnum.EN_COURS_DE_TRAITEMENT,
  EtatWorkflowIncidentEnum.EN_PAUSE,
  EtatWorkflowIncidentEnum.ABANDONNEE,
  EtatWorkflowIncidentEnum.CLOTUREE_PRESTATAIRE,
  EtatWorkflowIncidentEnum.CLOTUREE_VIPARIS,
  EtatWorkflowIncidentEnum.EN_ATTENTE_DE_DEVIS,
  EtatWorkflowIncidentEnum.DEVIS_ENVOYE,
]

const StatusFilter = ({ selected, onStatusSelected }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const [local, setLocal] = React.useState(selected)
  const [search, setSearch] = React.useState('')

  const data = React.useMemo(
    () =>
      DATA.map((p) => ({ label: i18n.t(`incidentStatus.${p}`), value: p }))
        .filter((p) => sanitize(p.label).includes(sanitize(search)))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [DATA, search]
  )

  React.useEffect(() => {
    setLocal(selected)
  }, [selected])

  const onItemSelected = (item: EtatWorkflowIncidentEnum) => {
    const isSelected = local.includes(item)

    setLocal(isSelected ? local.filter((id) => id !== item) : [...local, item])
  }

  const onSelectAll = () => {
    if (allSelected) {
      setLocal([])
    } else {
      setLocal(data.map((d) => d.value))
    }
  }

  const allSelected = data.every((st) => local.includes(st.value))

  return (
    <>
      <SearchContainer>
        <TextInput
          onChange={setSearch}
          searchStyle={bp === 'phone' ? 'onLight' : 'onDark'}
          left={{ name: 'search' }}
          placeholder={i18n.t('common.search')}
          value={search}
        />
      </SearchContainer>

      <CheckboxContainer>
        <Checkbox
          label={i18n.t('common.selectAll')}
          value={allSelected}
          onClick={onSelectAll}
          iconOnTheRight
          fontBold
        />
      </CheckboxContainer>

      <FilterView>
        <ListPicker data={data} onItemSelected={onItemSelected} selected={local} />
      </FilterView>
      <Footer>
        {local.length > 0 ? (
          <TouchableOpacity activeOpacity={0.9} onPress={() => setLocal([])}>
            <Text>{i18n.t('common.erase')}</Text>
          </TouchableOpacity>
        ) : (
          <View />
        )}
        <Button label={i18n.t('common.save')} onClick={() => onStatusSelected(local)} status="active" />
      </Footer>
    </>
  )
}

export default StatusFilter

const SearchContainer = styled(View)`
  padding: 16px 16px 0;
`

const FilterView = styled(View)`
  flex: 1;
  padding: 8px 22px 22px;
`

const Footer = styled(View)`
  border-top-width: 1px;
  border-top-color: ${(props) => props.theme.colors.abandoned};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 16px 31px;
`

const CheckboxContainer = styled(View)`
  padding: 8px 16px 0;
  flex-direction: row;
  justify-content: flex-end;
`
